import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";

import Swal from "sweetalert2";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useCalibrationCertificateViewModel } from "../../../viewmodels/operation/calibrationcertificate/useCalibrationCertificateViewModel";

import { generateCalibrationCertificatePdf } from "./CalibrationCertificatePdf";

const CalibrationCertificates = () => {
  const navigate = useNavigate();

  const [, setSearchParams] = useSearchParams();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const {
    calibrationCertificates,
    loading,

    page,
    setPage,

    search,
    setSearch,

    deleteCalibrationCertificate,

    getCalibrationCertificatesByIds,
  } = useCalibrationCertificateViewModel();

  const changePage = (newPage: number) => {
    setPage(newPage);

    setSearchParams({
      page: String(newPage),
      search,
    });
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete Calibration Certificate?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteCalibrationCertificate(id);

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Calibration certificate deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Unable to delete calibration certificate.",
      });
    }
  };

  const handlePrintSelected = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Selection",
        text: "Please select at least one certificate.",
      });

      return;
    }

    const certificates = await getCalibrationCertificatesByIds(selectedIds);

    if (!certificates?.length) {
      Swal.fire({
        icon: "error",
        title: "No Data",
        text: "Unable to load certificates.",
      });

      return;
    }

    generateCalibrationCertificatePdf(certificates);
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Calibration Certificates</h1>

            <p className="text-gray-500 mt-1">
              Manage calibration certificates
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handlePrintSelected}
              className="bg-green-600 text-white px-5 py-3 rounded-2xl"
            >
              Print PDF
            </button>

            <button
              onClick={() => navigate("/calibration-certificates/create")}
              className="bg-primary text-white px-5 py-3 rounded-2xl"
            >
              + Create
            </button>
          </div>
        </div>

        {/* SEARCH */}
        <div className="bg-white border rounded-2xl p-4 mb-5">
           <label className="block text-sm font-medium mb-2">Search</label>
          <input
            value={search}
            onChange={(e) => {
              const value = e.target.value;

              setSearch(value);
              setPage(1);

              setSearchParams({
                page: "1",
                search: value,
              });
            }}
            placeholder="Search..."
            className="w-full border rounded-xl px-4 py-3"
          />
        </div>

        {/* TABLE */}
        <div className="bg-white border rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="p-3 border w-12">
                  <input
                    type="checkbox"
                    checked={
                      calibrationCertificates.length > 0 &&
                      selectedIds.length === calibrationCertificates.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(
                          calibrationCertificates.map((x: any) => x.id),
                        );
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                  />
                </th>

                <th className="p-3 border">No</th>

                <th className="p-3 border">Customer</th>

                <th className="p-3 border">Report No</th>

                <th className="p-3 border">Date</th>

                <th className="p-3 border">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center p-6">
                    Loading...
                  </td>
                </tr>
              ) : calibrationCertificates.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center p-8 border border-slate-200 text-gray-500"
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                calibrationCertificates.map((x: any) => (
                  <tr key={x.id}>
                    <td className="p-3 border text-center">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(x.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds((prev) => [...prev, x.id]);
                          } else {
                            setSelectedIds((prev) =>
                              prev.filter((id) => id !== x.id),
                            );
                          }
                        }}
                      />
                    </td>

                    <td className="p-3 border">{x.no}</td>

                    <td className="p-3 border">{x.customerName}</td>

                    <td className="p-3 border">{x.reportNo}</td>

                    <td className="p-3 border">
                      {x.date
                        ? new Date(x.date).toLocaleDateString("en-GB")
                        : "-"}
                    </td>

                    <td className="p-3 border">
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            navigate(
                              `/calibration-certificates/view/${x.id}?page=${page}&search=${encodeURIComponent(search)}`,
                            )
                          }
                          className="bg-blue-500 text-white px-3 py-2 rounded-lg"
                        >
                          View
                        </button>

                        <button
                          onClick={() =>
                            navigate(
                              `/calibration-certificates/edit/${x.id}?page=${page}&search=${encodeURIComponent(search)}`,
                            )
                          }
                          className="bg-yellow-500 text-white px-3 py-2 rounded-lg"
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => handleDelete(x.id)}
                          className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg transition"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            disabled={page === 1}
            onClick={() => changePage(Math.max(1, page - 1))}
            className="px-4 py-2 border rounded-xl disabled:opacity-50"
          >
            Prev
          </button>

          <span className="text-sm font-medium">Page {page}</span>

          <button
            onClick={() => changePage(page + 1)}
            className="px-4 py-2 border rounded-xl"
          >
            Next
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CalibrationCertificates;
